import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import {
  ObservationLogFormData,
  ObservationLogFormActions,
} from '@/components/forms'
import {
  ControlledTextField,
  ControlledDateTimeField,
  ControlledSelect,
  ControlledMediaUpload,
} from '@/components/inputs'
import { yupResolver } from '@hookform/resolvers/yup'
import { schema } from './validationSchema'
import { FormComponentProps, UserGroups } from '@/types'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import {
  SAMPLE_POINT_COUNTING_METHOD_OPTIONS,
  OTHER_COUNTING_OPTION_OPTION_VALUE,
} from '../fieldsOptions'
import { useAuth } from '@/hooks'

export const SamplePointForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const { hasPermission } = useAuth()

  const methods = useForm<ObservationLogFormData>({
    mode: 'onChange',
    defaultValues: {
      counting_method: '',
      notes: '',
      observed_at: '',
      photos: undefined,
    },
    values: defaultValues,
    resolver: yupResolver(schema),
  })

  useApiErrorData<ObservationLogFormData>(apiErrorData, methods)

  const watchedCountingMethod = methods.watch('counting_method')

  const disabledForTheUser = !hasPermission([
    UserGroups.MANAGER,
    UserGroups.EMPLOYEE,
    UserGroups.DATA_CONTROLLER,
  ])

  return (
    <FormProvider {...methods}>
      <Stack mt={4} width="100%" alignItems="stretch" spacing={4}>
        <ControlledSelect
          control={methods.control}
          name="counting_method"
          label={t('observation-log.fieldLabels.countingMethod')}
          options={SAMPLE_POINT_COUNTING_METHOD_OPTIONS}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
        {watchedCountingMethod === OTHER_COUNTING_OPTION_OPTION_VALUE && (
          <ControlledTextField
            control={methods.control}
            name="counting_method2"
            placeholder={t('observation-log.fieldLabels.otherCountingMethod')}
            disabled={disabledForTheUser}
            errorMessagePrefix=""
          />
        )}

        <ControlledTextField
          control={methods.control}
          name="notes"
          placeholder={t('observation-log.fieldLabels.notes')}
          multiline={true}
          rows={3}
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />

        <ControlledDateTimeField
          name="observed_at"
          control={methods.control}
          label={t('observation-log.fieldLabels.observedAt')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />

        <ControlledMediaUpload
          name="photos"
          control={methods.control}
          label={t('observation-log.fieldLabels.media')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />

        <ObservationLogFormActions
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          isLoading={isLoading}
          methods={methods}
        />
      </Stack>
    </FormProvider>
  )
}
